/**
 * Adapter hooks that bridge the Instagram-specific API hooks to the
 * generic InboxHooks interface expected by shared-inbox components.
 */
import type { InboxHooks, GenericConversation, GenericMessage, GenericAgentConfig } from '@/components/shared-inbox/types';
import type { ConversationStatus } from '@/types/outreach';
import {
    useInstagramConversations,
    useInstagramConversationDetail,
    useInstagramConversationMessages,
    useInstagramSendAdminMessage,
    useInstagramUpdateConversationStatus,
    useInstagramMarkConversationRead,
    useInstagramAgentConfigs,
    useInstagramSaveAgentConfig,
    useInstagramDeleteAgentConfig,
} from './useInstagramInbox';

// ── Conversation list ────────────────────────────────────────────────────────

function useConversations(search?: string, status?: ConversationStatus | '') {
    const result = useInstagramConversations(search, status);
    return {
        ...result,
        data: result.data
            ? {
                  ...result.data,
                  pages: result.data.pages.map((page) => ({
                      count: page.count,
                      results: page.results.map(
                          (c): GenericConversation => ({
                              id: c.id,
                              // primaryId shows @username if available, else igScopedId
                              primaryId: c.customerUsername
                                  ? `@${c.customerUsername}`
                                  : c.igScopedId,
                              secondaryId: null,
                              customerName: c.customerName,
                              profilePicUrl: c.profilePicUrl,
                              status: c.status,
                              unreadCount: c.unreadCount,
                              updatedAt: c.updatedAt,
                              place: c.place,
                              lastMessage: c.lastMessage,
                              isWindowOpen: c.isWindowOpen,
                              windowExpiresAt: c.windowExpiresAt,
                          }),
                      ),
                  })),
              }
            : undefined,
    };
}

// ── Conversation detail ──────────────────────────────────────────────────────

function useConversationDetail(id: string | null) {
    const result = useInstagramConversationDetail(id);
    return {
        data: result.data
            ? ({
                  id: result.data.id,
                  primaryId: result.data.customerUsername
                      ? `@${result.data.customerUsername}`
                      : result.data.igScopedId,
                  secondaryId: null,
                  customerName: result.data.customerName,
                  profilePicUrl: result.data.profilePicUrl,
                  status: result.data.status,
                  unreadCount: result.data.unreadCount,
                  updatedAt: result.data.updatedAt,
                  place: result.data.place,
                  lastMessage: result.data.lastMessage,
                  isWindowOpen: result.data.isWindowOpen,
                  windowExpiresAt: result.data.windowExpiresAt,
              } satisfies GenericConversation)
            : undefined,
    };
}

// ── Messages ─────────────────────────────────────────────────────────────────

function useConversationMessages(id: string | null) {
    const result = useInstagramConversationMessages(id);
    return {
        isLoading: result.isLoading,
        data: result.data
            ? {
                  count: result.data.count,
                  results: result.data.results.map(
                      (m): GenericMessage => ({
                          id: m.id,
                          conversationId: m.conversationId,
                          direction: m.direction,
                          messageType: m.messageType,
                          senderType: m.senderType,
                          content: m.content,
                          mediaUrl: m.mediaUrl,
                          timestamp: m.timestamp,
                          storyUrl: m.storyUrl,
                      }),
                  ),
              }
            : undefined,
    };
}

// ── Mutations ────────────────────────────────────────────────────────────────

const useSendAdminMessage = useInstagramSendAdminMessage;
const useUpdateConversationStatus = useInstagramUpdateConversationStatus;
const useMarkConversationRead = useInstagramMarkConversationRead;

// ── Agent configs ────────────────────────────────────────────────────────────

function useAgentConfigs() {
    const result = useInstagramAgentConfigs();
    return {
        data: result.data as GenericAgentConfig[] | undefined,
    };
}

function useSaveAgentConfig() {
    const mutation = useInstagramSaveAgentConfig();
    return {
        isPending: mutation.isPending,
        mutate: (
            config: GenericAgentConfig & { name: string; systemPrompt: string },
            options?: { onSuccess?: (saved: GenericAgentConfig) => void },
        ) => {
            mutation.mutate(config as Parameters<typeof mutation.mutate>[0], {
                onSuccess: (saved) => options?.onSuccess?.(saved as GenericAgentConfig),
            });
        },
    };
}

function useDeleteAgentConfig() {
    const mutation = useInstagramDeleteAgentConfig();
    return {
        isPending: mutation.isPending,
        mutate: (id: string, options?: { onSuccess?: () => void }) => {
            mutation.mutate(id, { onSuccess: options?.onSuccess });
        },
    };
}

// ── Export as InboxHooks ─────────────────────────────────────────────────────

export const instagramInboxHooks: InboxHooks = {
    useConversations,
    useConversationDetail,
    useConversationMessages,
    useSendAdminMessage,
    useUpdateConversationStatus,
    useMarkConversationRead,
    useAgentConfigs,
    useSaveAgentConfig,
    useDeleteAgentConfig,
};
